import React from 'react';
import { Users, Eye, Clock, TrendingUp, Globe, Monitor } from 'lucide-react';

function OverviewStats({ data }) {
  if (!data) {
    return <div className="text-center py-8 text-gray-500">No analytics data available</div>;
  }

  const stats = [
    {
      title: 'Total Visitors',
      value: data.totalVisitors || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
    },
    {
      title: 'Total Page Views',
      value: data.totalPageViews || 0,
      icon: Eye,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
    },
    {
      title: 'Average Session Duration',
      value: data.averageSessionDuration
        ? `${Math.round(data.averageSessionDuration / 1000)}s`
        : '0s',
      icon: Clock,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
    },
    {
      title: 'Bounce Rate',
      value: data.bounceRate ? `${Math.round(data.bounceRate)}%` : '0%',
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
    },
  ];

  const topPage = data.topPages && data.topPages.length > 0 ? data.topPages[0] : null;

  return (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className={`bg-white border-2 ${stat.borderColor} rounded-lg p-6 hover:shadow-md transition-shadow`}
            >
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Top Pages and Additional Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Popular Page */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Monitor className="w-5 h-5 text-gray-500 mr-2" />
            Most Popular Page
          </h3>
          {topPage ? (
            <div>
              <p className="text-sm text-gray-600 mb-2">Page URL:</p>
              <p className="font-mono text-sm bg-gray-50 p-2 rounded mb-3 break-all">
                {topPage.page}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-purple-600">{topPage.count}</span>
                <span className="text-sm text-gray-500">page views</span>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No page data available</p>
          )}
        </div>

        {/* Quick Stats */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Globe className="w-5 h-5 text-gray-500 mr-2" />
            Quick Stats
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Unique Visitors</span>
              <span className="font-semibold text-gray-900">{data.totalVisitors || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Sessions</span>
              <span className="font-semibold text-gray-900">
                {data.sessions ? data.sessions.length : 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Pages per Session</span>
              <span className="font-semibold text-gray-900">
                {data.totalVisitors > 0
                  ? Math.round(((data.totalPageViews || 0) / data.totalVisitors) * 10) / 10
                  : 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Returning Visitors</span>
              <span className="font-semibold text-gray-900">
                {data.returningVisitors ? data.returningVisitors.size : 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Pages List */}
      {data.topPages && data.topPages.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Pages</h3>
          <div className="space-y-3">
            {data.topPages.slice(0, 5).map((page, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{page.page}</p>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-500">{page.count} views</span>
                  <div className="w-16 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full"
                      style={{
                        width: `${Math.min(100, (page.count / data.topPages[0].count) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default OverviewStats;
